import { useCallback, useEffect, useState } from 'react';
import { listCompanies } from '../services/configuration/companiesService';

const listeners = new Set();

let catalogState = {
  companies: [],
  companyNameByCnpj: new Map(),
  loaded: false,
  loading: false,
  error: null,
  request: null,
};

const notify = () => {
  listeners.forEach((listener) => listener(catalogState));
};

const normalizeCnpj = (value) => String(value ?? '').replace(/\D/g, '');

const normalizeCompaniesResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.body)) return response.body;
  return [];
};

const buildCompaniesMap = (companies) => {
  const map = new Map();
  (companies ?? []).forEach((company) => {
    const cnpj = normalizeCnpj(company?.cnpj);
    const empresa = String(company?.empresa ?? '').trim();
    if (cnpj && empresa) {
      map.set(cnpj, empresa);
    }
  });
  return map;
};

const updateCatalogState = (partial) => {
  catalogState = { ...catalogState, ...partial };
  notify();
};

const fetchCompaniesCatalog = async (force = false) => {
  if (!force && catalogState.loaded) return catalogState;
  if (catalogState.request) return catalogState.request;

  updateCatalogState({ loading: true, error: null });

  const request = (async () => {
    try {
      const response = await listCompanies();
      const companies = normalizeCompaniesResponse(response);
      const companyNameByCnpj = buildCompaniesMap(companies);

      updateCatalogState({
        companies,
        companyNameByCnpj,
        loaded: true,
        loading: false,
        error: null,
      });

      return catalogState;
    } catch (error) {
      updateCatalogState({ loading: false, error });
      throw error;
    } finally {
      catalogState = { ...catalogState, request: null };
    }
  })();

  catalogState = { ...catalogState, request };
  return request;
};

export const invalidateCompaniesCatalog = () => {
  updateCatalogState({
    companies: [],
    companyNameByCnpj: new Map(),
    loaded: false,
    loading: false,
    error: null,
    request: null,
  });
};

export const refreshCompaniesCatalog = () => fetchCompaniesCatalog(true);

export const useCompaniesCatalog = () => {
  const [state, setState] = useState(catalogState);

  useEffect(() => {
    const listener = (nextState) => setState(nextState);
    listeners.add(listener);

    if (!catalogState.loaded && !catalogState.loading) {
      fetchCompaniesCatalog().catch(console.error);
    }

    return () => {
      listeners.delete(listener);
    };
  }, []);

  const resolveCompanyName = useCallback(
    (cnpj, fallback = '') => {
      if (!cnpj) return fallback;
      const digits = normalizeCnpj(cnpj);
      return state.companyNameByCnpj.get(digits) ?? fallback;
    },
    [state.companyNameByCnpj]
  );

  return {
    companies: state.companies,
    companyNameByCnpj: state.companyNameByCnpj,
    isLoading: state.loading,
    error: state.error,
    resolveCompanyName,
    refreshCompaniesCatalog,
  };
};
